/**
 * What the drafter did about each finding.
 *
 * Findings are recomputed from scratch on every run; the DECISIONS about them
 * are the only thing worth keeping, and `findingId` is what lets them survive a
 * re-run. Without stable identity this store would resurrect a set-aside
 * finding on the next keystroke, which is the failure that makes people turn a
 * checker off.
 *
 * One outcome, `aside`: a person looked and decided it stands. On a playbook
 * finding that is somebody knowingly overriding their own floor, which is
 * exactly the kind of thing a reviewer coming after needs to be able to see.
 * `fixed` is still read, because stores written before it was removed carry it,
 * and it always meant the same thing in practice — seen, no longer asking.
 *
 * lp-016 §5. THE SERVER IS THE STORE NOW. It used to be localStorage, with two
 * consequences that were both real: a colleague opening the same lease saw none
 * of your set-asides and was asked the same thirty questions you had already
 * answered, and "who decided to leave that blank in, and when" had no answer at
 * all — on a document that goes to a counterparty, which is the situation this
 * check exists for.
 *
 * localStorage did not go away; it changed jobs. It is now a mirror, so that a
 * decision recorded while the network is down still holds for the person who
 * made it, and so the panel has something to show before the list comes back.
 * Reads prefer the server, writes go to both, and a failed write leaves the
 * local copy standing rather than silently dropping what somebody decided.
 */
export const OUTCOMES = Object.freeze({ FIXED: 'fixed', ASIDE: 'aside' });

const VERSION = 1;

function storageKey(leaseId) {
  return `lp.checks.${VERSION}.${leaseId}`;
}

function canonical(outcome) {
  return outcome === OUTCOMES.FIXED ? OUTCOMES.ASIDE : outcome;
}

function readMirror(leaseId) {
  try {
    const raw = window.localStorage.getItem(storageKey(leaseId));
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return {};

    Object.keys(parsed).forEach(id => {
      const record = parsed[id];
      if (record) record.outcome = canonical(record.outcome);
    });

    return parsed;
  } catch (error) {
    // A private window, cleared site data, or storage the browser refuses.
    // The checker works without memory; it must not fail without it.
    return {};
  }
}

function writeMirror(leaseId, decisions) {
  try {
    window.localStorage.setItem(storageKey(leaseId), JSON.stringify(decisions));
  } catch (error) {
    // Quota, or a browser that will not store. The decision still holds for
    // this sitting and the server already has it, which is the part that
    // matters to everyone else.
  }
}

/**
 * @param leaseId  which lease's decisions these are
 * @param remote   { list, record, clear }, each returning a promise. Omitted in
 *                 tests and on an unsaved lease, where the mirror is the whole
 *                 store — the panel has to work before a document has an id.
 */
export function createDecisions(leaseId, remote) {
  let decisions = readMirror(leaseId);
  let loaded = false;

  function persist() {
    writeMirror(leaseId, decisions);
  }

  return {
    outcomeFor(findingId) {
      const record = decisions[findingId];
      return record ? record.outcome : null;
    },

    // Whether the server's copy has arrived. The panel uses it to know that
    // "no set-asides" is a fact rather than a page that has not loaded yet.
    isLoaded() {
      return loaded;
    },

    /**
     * Everyone's decisions on this lease, not just this browser's.
     *
     * The server wins on conflict, with one exception: a decision this browser
     * made that the server has never heard of is kept. That is the offline
     * write, and dropping it would quietly undo something a person did.
     */
    load() {
      if (!remote) {
        loaded = true;
        return Promise.resolve(decisions);
      }

      return remote
        .list()
        .then(function(response) {
          const rows = (response && response.data && response.data.decisions) || [];
          const merged = {};

          rows.forEach(row => {
            if (!row || !row.findingId) return;
            merged[row.findingId] = {
              outcome: canonical(row.outcome),
              at: row.at || null,
              by: row.byName || null,
            };
          });

          Object.keys(decisions).forEach(id => {
            if (!merged[id] && decisions[id] && decisions[id].pending)
              merged[id] = decisions[id];
          });

          decisions = merged;
          loaded = true;
          persist();
          return decisions;
        })
        .catch(function() {
          // The mirror stands. A panel that refuses to open because the
          // decisions endpoint is down is worse than one showing only what this
          // browser knows.
          loaded = true;
          return decisions;
        });
    },

    /**
     * @param context  what the finding SAID — family, severity, summary, the
     *                 section it was in. Sent because findings do not persist,
     *                 so without it the trail reads "someone set aside
     *                 integrity_blank_run:s41:____", which is a record of
     *                 nothing anyone can act on.
     */
    record(findingId, outcome, at, context) {
      if (!findingId) return Promise.resolve();
      if (outcome !== OUTCOMES.FIXED && outcome !== OUTCOMES.ASIDE)
        return Promise.resolve();

      const decided = canonical(outcome);
      // Shown before the round trip. A set-aside that takes a network hop to
      // appear reads as a control that did not work.
      decisions = {
        ...decisions,
        [findingId]: { outcome: decided, at: at || null, pending: true },
      };
      persist();

      if (!remote) return Promise.resolve();

      return remote
        .record({ findingId: findingId, outcome: decided, ...(context || {}) })
        .then(function() {
          if (decisions[findingId]) {
            decisions = {
              ...decisions,
              [findingId]: { ...decisions[findingId], pending: false },
            };
            persist();
          }
        })
        .catch(function() {
          // Left pending on purpose: `load` keeps a pending local decision the
          // server has never heard of, so the next open still shows it.
        });
    },

    // A drafter who set something aside by mistake needs a way back that is not
    // "reload and hope". Confirming is reversible everywhere else in this
    // product and there is no reason for this to be the exception.
    clear(findingId) {
      if (!decisions[findingId]) return Promise.resolve();

      const next = { ...decisions };
      delete next[findingId];
      decisions = next;
      persist();

      if (!remote) return Promise.resolve();
      return remote.clear(findingId).catch(function() {});
    },

    /**
     * Findings with their outcome attached, and the counts the panel reports.
     *
     * The counts name fixed and set aside separately rather than totalling them
     * into "addressed", because the progress line says both and a single number
     * would hide which.
     */
    apply(findings) {
      const marked = (findings || []).map(finding => ({
        ...finding,
        outcome: this.outcomeFor(finding.id),
        decidedBy: (decisions[finding.id] || {}).by || null,
      }));

      return {
        findings: marked,
        counts: {
          total: marked.length,
          open: marked.filter(f => !f.outcome).length,
          fixed: marked.filter(f => f.outcome === OUTCOMES.FIXED).length,
          aside: marked.filter(f => f.outcome === OUTCOMES.ASIDE).length,
        },
      };
    },
  };
}
